import React, { useRef } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import TranslationWrapper from "../TranslationWrapper/TranslationWrapper";
import { useDialog } from "../../views/Upsell/modernTS/JUI/Dialogs";
import { useBlocker } from "react-router";

/**
 * Summon an "are you sure" popup when the user requests to navigate away.
 *
 * @param when The popup will only show if this condition is true
 * @param propNavigate Optionally provide a custom destination for when the user chooses "yes" at the popup
 */
export function useRouteLeavingGuard({ when, navigate: propNavigate }: { when: boolean; navigate?: () => void }) {
  const openModal = useRouteLeavingGuardModal();
  const isOpen = useRef<boolean>(false);

  const blocker = useBlocker(when);

  if (!isOpen.current && blocker.state === "blocked") {
    isOpen.current = true;
    openModal().then((result) => {
      isOpen.current = false;
      if (result === "CONFIRMED") {
        if (propNavigate) {
          propNavigate();
        } else {
          blocker.proceed();
        }
      } else {
        blocker.reset();
      }
    });
  }
}

export function useRouteLeavingGuardModal() {
  const { createDialog, closeDialog } = useDialog();

  return () => {
    return new Promise<"CONFIRMED" | "CANCELED">((resolve) => {
      createDialog({
        name: "route-leaving-guard",
        maxWidth: "lg",
        children: (
          <RouteLeavingGuardModal
            onCancel={() => {
              resolve("CANCELED");
              closeDialog("route-leaving-guard");
            }}
            onConfirm={() => {
              resolve("CONFIRMED");
              closeDialog("route-leaving-guard");
            }}
          />
        ),
      });
    });
  };
}

function RouteLeavingGuardModal({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  return (
    <Dialog open={true}>
      <DialogTitle>
        <TranslationWrapper tKey={"Caution!"} />
      </DialogTitle>
      <DialogContent>
        <TranslationWrapper tKey={"Your changes are not saved. Do you still want to leave?"} />
      </DialogContent>
      <DialogActions>
        <Button color={"primary"} onClick={() => onCancel()} variant={"contained"}>
          <TranslationWrapper tKey={"No"} />
        </Button>
        <Button onClick={() => onConfirm()} variant={"outlined"}>
          <TranslationWrapper tKey={"Yes"} />
        </Button>
      </DialogActions>
    </Dialog>
  );
}
